/**
 * CloudSettings — section in settings panel for auth & cloud sync.
 *
 * Shows:
 * - If not authenticated: sign-in prompt with feature highlights
 * - If authenticated: user email, sync status, sync now + logout buttons
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getApiUrl } from '../utils/environment';
import { collectCloudSettings } from '../settings';
import styles from './CloudSettings.module.css';

interface CloudSettingsProps {
  /** Open the login flow */
  onSignIn: () => void;
}

function getApiBaseUrl(): string {
  return getApiUrl();
}

export function CloudSettings({ onSignIn }: CloudSettingsProps) {
  const { user, isAuthenticated, logout, getAuthHeaders } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  }, [logout]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncStatus(null);
    try {
      const baseUrl = getApiBaseUrl();
      const headers = getAuthHeaders();

      // Collect local settings to upload (uses the settings registry)
      const settings = collectCloudSettings();

      const res = await fetch(`${baseUrl}/api/user/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ settings }),
      });

      if (res.ok) {
        setSyncStatus('Synced ✓');
        setTimeout(() => setSyncStatus(null), 3000);
      } else {
        setSyncStatus('Sync failed');
        setTimeout(() => setSyncStatus(null), 3000);
      }
    } catch {
      setSyncStatus('Sync failed');
      setTimeout(() => setSyncStatus(null), 3000);
    } finally {
      setSyncing(false);
    }
  }, [getAuthHeaders]);

  if (!isAuthenticated || !user) {
    return (
      <div className={styles.container}>
        <div className={styles.signInPrompt}>
          <p className={styles.promptText}>
            Sign in to sync your settings across devices
          </p>
          <button
            type="button"
            className={styles.signInBtn}
            onClick={onSignIn}
          >
            ✉️ Sign in with email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.userInfo}>
        <div className={styles.userDetails}>
          <span className={styles.userEmail}>{user.email}</span>
          {syncStatus && <span className={styles.syncStatus}>{syncStatus}</span>}
        </div>
        <div className={styles.userActions}>
          <button
            type="button"
            className={styles.syncBtn}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? '⏳' : '🔄'} {syncing ? 'Syncing…' : 'Sync'}
          </button>
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={handleLogout}
            disabled={loggingOut}
          >
            {loggingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  );
}
